import { getMajorVersion } from 'helpers';
import checkIfKitIsUpdating from 'helpers/checkIfKitIsUpdating';
import useGetKitIdParam from 'hooks/useGetKitIdParam';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getKitById } from 'store/kits';
import { usePreviousKitMajorVersion } from './usePreviousKitMajorVersion';
import { useSystemInfo } from './useSystemInfo';

type Version = string | null;
type PrettyVersion = `v${string}` | null;

const formatVersion = (version: Version): PrettyVersion => {
  if (!version) return null;

  return `v${version.replace(/^v/i, '').replace(/\b0+(?=\d)/g, '')}`;
};

export default function useKitSystemInfo(kit_id?: string) {
  const kitId = useGetKitIdParam();
  const currentKit = useSelector(getKitById(kit_id ?? kitId));
  const { latestSciVersion } = useSystemInfo();
  const { hasMultipleVersions } = usePreviousKitMajorVersion();

  const {
    isUpdating,
    hasOutdatedKitVersion,
    prettyVersion,
    hasResultsReady,
    resultsViewable,
  } = useMemo(() => {
    const sciVersion = currentKit?.sci_version;
    const hasResultsReady = !!currentKit?.date_results_ready;
    const resultsViewable =
      !!currentKit?.date_survey_completed && hasResultsReady;

    // Base state: no version and results viewable
    const isUpdating = checkIfKitIsUpdating(currentKit);

    // Check if outdated (only when we have version and latest version)
    const hasOutdatedKitVersion =
      sciVersion && latestSciVersion && hasMultipleVersions
        ? getMajorVersion(sciVersion) !== getMajorVersion(latestSciVersion)
        : false;

    // Format version if it exists, regardless of results ready state
    const prettyVersion = formatVersion(sciVersion ?? null);

    return {
      isUpdating,
      hasOutdatedKitVersion,
      prettyVersion,
      hasResultsReady,
      resultsViewable,
    };
  }, [
    currentKit?.sci_version,
    currentKit?.date_results_ready,
    currentKit?.date_survey_completed,
    latestSciVersion,
    hasMultipleVersions,
  ]);

  return {
    isUpdating,
    hasOutdatedKitVersion,
    hasResultsReady,
    latestSciVersion,
    prettyVersion,
    currentKit,
    resultsViewable,
  };
}
